import React from 'react';
import { Phone, Mail, FileSignature, PhoneCall, ArrowRight, Star } from 'lucide-react';
import { EnPowerLogoText } from '../../EnPowerLogo';
import { PageFooterSpeicher, isBessOffer } from './ContentPage2Speicher';
import { DraggableElement } from '../../DraggableElement';

const DATA = {
  ansprechpartner: 'Max Mustermann',
  telefonnummer: '+49 (0) 123 456 789',
  email: 'info@enpower.de',
  gueltig_bis: '15. März 2025',
};

const kleinspeicherFaqs = [
  { q: 'Wie groß ist ein Speicher?', a: 'Die reine Stellfläche eines 125-kW-Speichers beträgt maximal 5 m². Zwei Einheiten (250 kW) benötigen maximal 10 m² und passen auf jedes Gewerbegrundstück.' },
  { q: 'Brauche ich eine Baugenehmigung?', a: 'In der Regel nein. Im Gegensatz zu Großspeichern ist für Kleinspeicher keine klassische Baugenehmigung erforderlich – ein erheblicher Zeit- und Kostenvorteil.' },
  { q: 'Was passiert nach 20 Jahren?', a: 'Sie haben zwei Mal die Option auf eine 5-jährige Verlängerung. In dieser Phase erhalten Sie 5% vom Ertrag des Speichers. Alternativ wird die Anlage auf unsere Kosten rückgebaut.' },
  { q: 'Kann die Anlage erweitert werden?', a: 'Ja. Sollte das Stromnetz vor Ort Kapazitäten bis zu 1 MW zulassen, kann die Anlage entsprechend skaliert werden. Ihre Vergütung steigt dann proportional.' },
  { q: 'Welche Kosten entstehen mir?', a: 'Keine. EN:POWER übernimmt sämtliche Kosten: Installation, Netzanschluss, Betrieb, Wartung, Monitoring und Versicherung. Sie stellen nur die Stellfläche bereit.' },
  { q: 'Ist das Angebot verbindlich?', a: 'Nein. Das Indikationsangebot ist unverbindlich und dient als Gesprächsgrundlage. Verbindlich wird es erst nach beidseitiger Unterzeichnung des Stellflächenvertrags.' },
];

const bessFaqs = [
  { q: 'Wer setzt das Projekt um?', a: 'Das BESS-Projekt ist ein Gemeinschaftsprojekt von EN:POWER und Solarea. EN:POWER übernimmt Akquise, Netzplanung und IAB-Investoren; Solarea verantwortet Baurecht, Großinvestoren und Fonds.' },
  { q: 'Brauche ich eine Baugenehmigung?', a: 'Ja. Für den BESS-Großspeicher ist vor Baubeginn eine Baugenehmigung einzuplanen. Solarea übernimmt die baurechtliche Entwicklung und stimmt die erforderlichen Unterlagen ab.' },
  { q: 'Was passiert vor dem Bau?', a: 'Nach Vertragsabschluss laufen Netzprüfung, technische Detailplanung und baurechtliche Entwicklung. EN:POWER führt die Netzplanung, Solarea den Genehmigungsweg. Erst danach beginnen Aufstellung und Anschluss.' },
  { q: 'Welche Kosten entstehen mir?', a: 'Keine. EN:POWER und Solarea übernehmen Projektprüfung, Netzplanung, Baurecht und Vermarktung; Kosten für Genehmigung, Installation, Netzanschluss, Betrieb, Wartung, Monitoring und Versicherung liegen nicht bei Ihnen.' },
  { q: 'Wie groß ist ein BESS?', a: 'Die konkrete Dimensionierung hängt von Netzkapazität, Stellfläche und Genehmigungsrahmen ab. Die angegebenen Flächen und Leistungen sind indikativ.' },
  { q: 'Was passiert nach 20 Jahren?', a: 'Sie haben zwei Mal die Option auf eine 5-jährige Verlängerung. Alternativ wird die Anlage auf unsere Kosten rückgebaut.' },
];

export const ContentPage4Speicher = ({ data = {}, editMode = false, showMarkers = true, selectedElements = [], elementPositions = {}, onSelectElement, onPositionChange }) => {
  const offerData = { ...DATA, ...data };
  const isBess = isBessOffer(offerData);
  const faqItems = isBess ? bessFaqs : kleinspeicherFaqs;
  const authorizationRecipient = offerData.email || 'info@enpower.de';
  const alternativeCards = isBess
    ? [
        { title: 'Skalierung nach Netzkapazität', desc: 'Die BESS-Leistung wird anhand von Netzanschluss, Stellfläche und Genehmigungsrahmen dimensioniert. EN:POWER führt die netztechnische Planung.', hint: 'Netzplanung', color: '#E07000' },
        { title: 'Baurecht & Vermarktung', desc: 'Solarea entwickelt den Standort baurechtlich und vermarktet Projekte an Großinvestoren und Fonds mit langjähriger Markterfahrung.', hint: 'Fondsvertrieb', color: '#122A5C' },
      ]
    : [
        { title: 'Skalierung auf 500 kW – 1 MW', desc: 'Bei ausreichender Netzkapazität können weitere Einheiten hinzugefügt werden. Die Vergütung skaliert proportional – bis zu 4× höher bei 1 MW.', hint: 'Maximale Vergütung', color: '#E07000' },
        { title: 'Kombination mit PV-Anlage', desc: 'Haben Sie zusätzlich eine geeignete Dach- oder Freifläche? EN:POWER bietet auch PV-Anlagen als Ergänzung – für maximale Wertschöpfung.', hint: 'Doppelte Einnahmen', color: '#122A5C' },
      ];
  const nextSteps = isBess
    ? [
        { num: '01', icon: FileSignature, active: true, title: 'Flächennutzungsvertrag', desc: 'Im ersten Schritt schließen wir den Flächennutzungsvertrag ab. Dieser regelt Nutzung, Vergütung, Laufzeit und die Grundlage für das Gemeinschaftsprojekt.', action: null },
        { num: '02', icon: PhoneCall, active: false, title: 'Netzprüfung & Baurecht', desc: `Nach Vertragsabschluss übernimmt EN:POWER die Netzplanung; Solarea führt die baurechtliche Entwicklung und Vermarktung an Großinvestoren sowie Fonds.`, action: `Vollmacht an: ${authorizationRecipient}` },
      ]
    : [
        { num: '01', icon: FileSignature, active: true, title: 'Flächennutzungsvertrag', desc: 'Im ersten Schritt schließen wir den Flächennutzungsvertrag ab. Dieser regelt die Nutzung Ihrer Stellfläche sowie die Höhe und Laufzeit Ihrer Vergütung.', action: null },
        { num: '02', icon: PhoneCall, active: false, title: 'Anfrage beim Netzbetreiber', desc: 'Nach Abschluss des Vertrages prüfen wir die Netzeinspeisung und kommunizieren direkt mit Ihrem Netzbetreiber. Wir benötigen nur Ihre Vollmacht – Dauer: 6–8 Wochen.', action: `Vollmacht an: ${authorizationRecipient}` },
      ];
  const ctaHeadline = isBess ? 'Starten wir das Großspeicherprojekt gemeinsam mit Solarea.' : 'Starten wir die Netzprüfung.';
  const dragProps = {
    editMode,
    showMarkers,
    selectedElements,
    elementPositions,
    onSelectElement,
    onPositionChange,
    dragAxis: 'y',
  };

  return (
  <div className="a4-page" data-testid="page-4-sp" style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
    <DraggableElement id="SP-S4-Alternatives-Heading" {...dragProps}>
    <div style={{ padding: '28px 48px 0', background: 'white', flexShrink: 0 }}>
      <span className="section-overline">Weitere Möglichkeiten</span>
      <h2 className="proposal-h2" style={{ fontSize: '19px', marginBottom: 0 }}>SKALIERUNG & OPTIONEN</h2>
      <div className="orange-rule" style={{ marginBottom: 0 }} />
    </div>
    </DraggableElement>

    <DraggableElement id="SP-S4-Alternatives" {...dragProps}>
    <div style={{ padding: '12px 48px 18px', background: 'white', flexShrink: 0 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', height: '121px', boxSizing: 'border-box' }}>
        <DraggableElement id="SP-S4-Alternative-1" {...dragProps}>
        <div style={{ padding: '12px 14px', background: '#F8FAFC', borderRadius: '9px', border: '1px solid #E2E8F0', borderLeft: '3px solid #E07000', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <div>
            <h3 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '11px', fontWeight: 700, color: '#122A5C', marginBottom: '5px' }}>{alternativeCards[0].title}</h3>
            <p className="proposal-body" style={{ fontSize: '9.5px' }}>{alternativeCards[0].desc}</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginTop: '6px' }}>
            <ArrowRight size={10} color="#E07000" />
            <span style={{ fontFamily: 'Manrope, sans-serif', fontSize: '8.5px', fontWeight: 600, color: '#E07000' }}>{alternativeCards[0].hint}</span>
          </div>
        </div>
        </DraggableElement>

        <DraggableElement id="SP-S4-Alternative-2" {...dragProps}>
        <div style={{ padding: '12px 14px', background: '#F8FAFC', borderRadius: '9px', border: '1px solid #E2E8F0', borderLeft: '3px solid rgba(18,42,92,0.3)', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <div>
            <h3 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '11px', fontWeight: 700, color: '#122A5C', marginBottom: '5px' }}>{alternativeCards[1].title}</h3>
            <p className="proposal-body" style={{ fontSize: '9.5px' }}>{alternativeCards[1].desc}</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginTop: '6px' }}>
            <ArrowRight size={10} color="#122A5C" />
            <span style={{ fontFamily: 'Manrope, sans-serif', fontSize: '8.5px', fontWeight: 600, color: '#122A5C' }}>{alternativeCards[1].hint}</span>
          </div>
        </div>
        </DraggableElement>

        <DraggableElement id="SP-S4-Contact" {...dragProps}>
        <div style={{ padding: '12px 14px', background: '#F8FAFC', borderRadius: '9px', border: '1px solid #E2E8F0', borderLeft: '3px solid rgba(18,42,92,0.3)', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', overflow: 'hidden' }}>
          <div>
            <h3 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '11px', fontWeight: 700, color: '#122A5C', marginBottom: '5px' }}>Ihr Ansprechpartner</h3>
            <p className="proposal-body" style={{ fontSize: '10px', marginBottom: '6px', fontWeight: 500, color: '#122A5C' }}>{offerData.ansprechpartner} - EN:POWER</p>
            <p className="proposal-body" style={{ fontSize: '9.5px', color: '#475569', lineHeight: 1.5 }}>{offerData.telefonnummer}<br />{offerData.email}</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginTop: '6px' }}>
            <ArrowRight size={10} color="#122A5C" />
            <span style={{ fontFamily: 'Manrope, sans-serif', fontSize: '8.5px', fontWeight: 600, color: '#122A5C' }}>Direkter Kontakt</span>
          </div>
        </div>
        </DraggableElement>
      </div>
    </div>
    </DraggableElement>

    <div style={{ height: '1px', background: '#E2E8F0', flexShrink: 0, margin: '0 48px' }} />

    <DraggableElement id="SP-S4-NextSteps" {...dragProps}>
    <div style={{ padding: '18px 48px 0', background: '#F8FAFC', flexShrink: 0 }}>
      <span className="section-overline">Ihr Weg zum Speicherprojekt</span>
      <h2 className="proposal-h2" style={{ fontSize: '19px', marginBottom: 0 }}>SO GEHT'S WEITER</h2>
      <div className="orange-rule" style={{ marginBottom: 0 }} />
    </div>

    <div style={{ padding: '12px 48px 18px', background: '#F8FAFC', flexShrink: 0 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', height: '110px', boxSizing: 'border-box' }}>
        {nextSteps.map((step, i) => {
          const Icon = step.icon;
          return (
            <DraggableElement key={i} id={`SP-S4-Step-${i + 1}`} {...dragProps}>
            <div style={{ display: 'flex', gap: '10px', padding: '10px 12px', background: 'white', borderRadius: '9px', border: `1.5px solid ${step.active ? 'rgba(224,112,0,0.25)' : '#E2E8F0'}`, height: '100%', boxSizing: 'border-box' }}>
              <div style={{ width: '28px', height: '28px', borderRadius: '50%', flexShrink: 0, border: `2px solid ${step.active ? '#E07000' : '#CBD5E1'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Outfit, sans-serif', fontWeight: 700, fontSize: '11px', color: step.active ? '#E07000' : '#94A3B8', background: 'white' }}>
                {step.num}
              </div>
              <div style={{ flex: 1, overflow: 'hidden' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '3px' }}>
                  <Icon size={10} color={step.active ? '#E07000' : '#64748B'} strokeWidth={2} />
                  <h3 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '10.5px', fontWeight: 700, color: '#122A5C', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{step.title}</h3>
                </div>
                <p className="proposal-body" style={{ fontSize: '9px', lineHeight: 1.45 }}>{step.desc}</p>
                {step.action && (
                  <div style={{ marginTop: '4px', display: 'inline-flex', alignItems: 'center', gap: '3px', background: 'rgba(224,112,0,0.07)', border: '1px solid rgba(224,112,0,0.18)', borderRadius: '4px', padding: '3px 8px', fontFamily: 'Manrope, sans-serif', fontSize: '8px', fontWeight: 600, color: '#92400E' }}>
                    &rarr; {step.action}
                  </div>
                )}
              </div>
            </div>
            </DraggableElement>
          );
        })}
      </div>
    </div>
    </DraggableElement>

    <div style={{ height: '1px', background: '#E2E8F0', flexShrink: 0, margin: '0 48px' }} />

    <DraggableElement id="SP-S4-FAQ" {...dragProps}>
    <div style={{ padding: '20px 48px 8px', background: 'white', flexShrink: 0 }}>
      <span className="section-overline">Häufige Fragen</span>
      <h2 className="proposal-h2" style={{ fontSize: '19px', marginBottom: 0 }}>HABEN SIE NOCH FRAGEN?</h2>
      <div className="orange-rule" style={{ marginBottom: 0 }} />
    </div>

    <div style={{ padding: '40px 48px', background: 'white', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', flexShrink: 0 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gridTemplateRows: 'auto auto', gap: '14px', width: '100%' }}>
        {faqItems.map((faq, i) => (
          <DraggableElement key={i} id={`SP-S4-FAQ-${i + 1}`} {...dragProps}>
          <div style={{ padding: '16px 15px', background: '#FAFBFC', borderRadius: '10px', border: '1px solid #E2E8F0', display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: '160px', maxHeight: '160px' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '7px', marginBottom: '8px', flexShrink: 0 }}>
              <div style={{ width: '20px', height: '20px', background: 'rgba(224,112,0,0.1)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '2px' }}>
                <span style={{ fontFamily: 'Outfit, sans-serif', fontSize: '9px', fontWeight: 700, color: '#E07000' }}>?</span>
              </div>
              <h3 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '10px', fontWeight: 700, color: '#122A5C', lineHeight: 1.3 }}>{faq.q}</h3>
            </div>
            <p className="proposal-body" style={{ fontSize: '9px', lineHeight: 1.55, color: '#64748B', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 6, WebkitBoxOrient: 'vertical' }}>{faq.a}</p>
          </div>
          </DraggableElement>
        ))}
      </div>
    </div>
    </DraggableElement>

    {/* CTA */}
    <DraggableElement id="SP-S4-CTA" {...dragProps}>
    <div style={{ margin: '10px 48px 0', background: '#122A5C', borderRadius: '10px', padding: '14px 20px', display: 'grid', gridTemplateColumns: '1fr auto', gap: '20px', alignItems: 'center', flexShrink: 0 }}>
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '4px' }}>
          <Star size={11} color="#E07000" fill="#E07000" />
          <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '7.5px', fontWeight: 700, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.1em', margin: 0 }}>Bereit für den nächsten Schritt?</p>
        </div>
        <p style={{ fontFamily: 'Outfit, sans-serif', fontSize: '14px', fontWeight: 700, color: 'white', marginBottom: '3px' }}>{ctaHeadline}</p>
        <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '9px', color: 'rgba(255,255,255,0.55)', margin: 0 }}>Ich freue mich auf Ihre Rückmeldung – <span style={{ color: 'white', fontWeight: 600 }}>{offerData.ansprechpartner}</span></p>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{ width: '20px', height: '20px', background: 'rgba(255,255,255,0.1)', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Phone size={9} color="white" /></div>
          <span style={{ fontFamily: 'Manrope, sans-serif', fontSize: '9.5px', color: 'white', fontWeight: 500 }}>{offerData.telefonnummer}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{ width: '20px', height: '20px', background: 'rgba(224,112,0,0.2)', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Mail size={9} color="#E07000" /></div>
          <span style={{ fontFamily: 'Manrope, sans-serif', fontSize: '9.5px', color: 'white', fontWeight: 500 }}>{offerData.email}</span>
        </div>
      </div>
    </div>
    </DraggableElement>

    <DraggableElement id="SP-S4-Signature" {...dragProps}>
    <div style={{ margin: '10px 48px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
      <div>
        <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '8.5px', color: '#94A3B8', marginBottom: '2px', fontStyle: 'italic' }}>Mit freundlichen Grüßen,</p>
        <p style={{ fontFamily: 'Outfit, sans-serif', fontSize: '12px', fontWeight: 700, color: '#122A5C', marginBottom: '2px' }}>{offerData.ansprechpartner}</p>
        <EnPowerLogoText size="sm" variant="dark" />
      </div>
      <div style={{ textAlign: 'right' }}>
        <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '7.5px', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '2px' }}>Gültig bis</p>
        <p style={{ fontFamily: 'Outfit, sans-serif', fontSize: '11px', fontWeight: 600, color: '#122A5C' }}>{offerData.gueltig_bis}</p>
      </div>
    </div>
    </DraggableElement>

    <PageFooterSpeicher page={4} variant={isBess ? 'bess' : offerData.offerVariant} />
  </div>
  );
};
